import React, { useState, useContext, useEffect } from 'react';
import {
  Radio,
  RadioGroup,
  Checkbox,
  Snackbar,
  FormControlLabel,
  FormLabel,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import cn from 'classnames';
import { useDrop } from 'react-dnd';
import addPhotoIcon from '../../assets/images/addPhoto.png';
import { DND_TYPES } from '../../enums';
import { AppContext } from '../../AppContext';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import FORM_HEADERS from '../../enums/formHeaders';
import { formatNumberWithCommas, parseNumberInputValue } from '../../helpers';
import { InspectorOrderReviewContext } from '../InspectorOrderReview/InspectorOrderReviewContext/InspectorOrderReviewContext';
import MobilePhotoPicker from '../InspectorOrderReview/MobilePhotoPicker';

const MARGIN_ABOVE_INPUT = 25;

const ColoredCheckbox = withStyles({
  root: {
    color: 'var(--jmi-green)',
    '&$checked': {},
  },
  checked: {},
})(props => <Checkbox color="default" {...props} />);

const ColoredRadio = withStyles({
  root: {
    color: 'var(--jmi-green)',
    '&$checked': {},
  },
  checked: {},
})(props => <Radio color="default" {...props} />);

const ColoredFormControlLabel = withStyles({
  // checked: {
  //   '&, & + $label': {
  //     color: 'blue',
  //   },
  // },
  label: {
    marginLeft: 0,
  },
})(props => <FormControlLabel {...props} />);

const ColoredTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: 'var(--jmi-blue)',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      color: 'var(--jmi-blue)',
      borderColor: 'var(--jmi-blue)',
    },
    // '& .MuiFormLabel-root:focus + fieldset': {
    //   borderColor: 'var(--jmi-blue)',
    // },
    '& input:valid:focus + fieldset': {
      borderColor: 'var(--jmi-blue)',
    },
  },
})(TextField);

export const ColoredInfoIcon = props => (
  <InfoIcon {...props} style={{ color: 'var(--jmi-green)', fontSize: 16, ...props.style }} />
);

const answerTypeAdornments = {
  Currency: {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  },
  Percentage: {
    endAdornment: <InputAdornment position="end">%</InputAdornment>,
  },
};

/**
 * @func parseIntWithFallback
 * @desc tries to parse and int and will either return the integer or a fallback value if the parsing failed
 * @desc used for parsing answerValueId since Material UI sends them as strings but we need to strict compare against an integer
 * @param {*} val
 * @param {*} fallback
 */
const parseIntWithFallback = (val, fallback = null) => parseInt(val) || fallback;

/**
 *
 * @param {*} question
 * @param {*} src
 * @param {*} answerTriggerIndex sent when using radio or multi-select (to determine if an image exists in the currently hovered dropzone instance)
 * @param { bool } imagesCanDuplicate used for radios and multiselect (cannot be duplicated on radios, but can be duplicated on multiselect)
 */
const imageIsAttached = (question, src, answerTrigger = null) => {
  // if answerTriggerIndex exists, it's going to be a multiselect

  let answerIndex = 0;

  // determine if this dropzone belongs to an active answer
  if (answerTrigger !== null) {
    // const answerTrigger = question.answerTriggers[answerTriggerIndex];
    answerIndex = question.answers.findIndex(ans => ans.answerValueId === answerTrigger.answerValueId);
  }

  return (
    question.answers &&
    question.answers.length &&
    question.answers[answerIndex] &&
    question.answers[answerIndex].formFieldRepAnswerImage.findIndex(img => {
      return [img.src, img.imageUri].includes(src);
    }) !== -1
  );
};

const imageIsAttachedToOtherAnswers = (question, src) => {
  return question.answers.reduce((acc, cur) => {
    // if true, just return since it can never go back to false
    if (acc) return acc;

    const answerImages = cur.formFieldRepAnswerImage.map(img => [img.src, img.imageUri].filter(u => u)).flat(); // get the src or image uri and filter out any undefined values
    const attached = !!answerImages.find(url => url === src);

    return attached;
  }, false);
};

const DndZone = ({ children, isRadio, question, onDropImage, answerTriggerIndex = null, canDrop: canDropFunc }) => {
  const bundleAndShipImage = ({ type, ...image }) => {
    // make sure the function exists first
    !!onDropImage && onDropImage(image);
  };

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: DND_TYPES.IMAGE,
    drop: bundleAndShipImage,
    collect: monitor => {
      const item = monitor.getItem();

      const canDrop = !imageIsAttached(question, item ? item.src : '');
      // const canDrop = !!canDropFunc ? canDropFunc(question, item ? item.src : '') : null;
      return item
        ? {
            isOver: !!monitor.isOver(),
            canDrop,
          }
        : {};
    },
  });

  return children({
    isOver,
    canDrop,
    ref: dropRef,
  });
};

const FieldRepInspectionForm = props => {
  const {
    classes,
    updateStepData,
    updateStepQuestions,
    removeImageFromQuestion,
    stepData: { questions, goToNextStep, orderLineId, useSnack, requestAddFormHeader, formHeaderId, rctquestions },
    isAusumOrLc360,
  } = props;

  /**
   * conditional rendering of content based on formHeader
   */
  const isCommericialNarratives = formHeaderId === FORM_HEADERS.COMMERCIAL_NARRATIVES || formHeaderId === FORM_HEADERS.COMMERCIAL_NARRATIVES_OB;

  // snackbar stuff
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const { state, dispatch } = useContext(AppContext);
  const {
    state: { isSelfInspect, order, orderForms, imagesWithQuestions },
    dispatch: reviewDispatch,
  } = useContext(InspectorOrderReviewContext);

  const updatePictureInput = (question, image = null) => {
    if (question.answers.length > 0) {
      const existingImages = question.answers[0].formFieldRepAnswerImage || [];
      let imageAlreadyAttached = false;

      if (image) {
        imageAlreadyAttached = imageIsAttached(question, image.src);
      }

      // question.answers[0].answerText = val;
      question.answers[0].formFieldRepAnswerImage =
        image && !imageAlreadyAttached ? existingImages.concat(image) : existingImages;
    } else {
      var answer = {
        orderFormHeaderId: question.orderFormHeaderId,
        questionId: question.questionId,
        answerValueId: null,
        answerText: null,
        formFieldRepAnswerImage: image ? [image] : [],
      };
      question.answers.push(answer);
    }

    // update state in the parent
    updateStepData(question);
  };

  const updateAnswerText = (question, val, image = null) => {
    if (question.answers.length > 0) {
      const existingImages = question.answers[0].formFieldRepAnswerImage || [];
      let imageAlreadyAttached = false;

      if (image) {
        imageAlreadyAttached = imageIsAttached(question, image.src);
      }

      question.answers[0].answerText = val;
      question.answers[0].formFieldRepAnswerImage =
        image && !imageAlreadyAttached ? existingImages.concat(image) : existingImages;
    } else {
      var answer = {
        orderFormHeaderId: question.orderFormHeaderId,
        questionId: question.questionId,
        answerValueId: null,
        answerText: val,
        formFieldRepAnswerImage: image ? [image] : [],
      };
      question.answers.push(answer);
    }

    // update state in the parent
    updateStepData(question);
  };

  const updateAnswerValue = (question, val = null, image = null, otherText = null) => {
    var answerTrigger = question.answerTriggers.find(at => at.answerValueId == val);

    const addAnswer = () => {
      if (question.answers.length === 0) {
        var answer = {
          orderFormHeaderId: question.orderFormHeaderId,
          questionId: question.questionId,
          answerValueId: val,
          answerText: null,
          value: answerTrigger.value,
          formFieldRepAnswerImage: image ? [image] : [],
        };
        question.answers.push(answer);
      }

      var triggeredSubs = answerTrigger.triggeredSub;
      if (triggeredSubs != null && triggeredSubs.length > 0) {
        var questionIndex = questions.findIndex(q => q.formDefinitionId == question.formDefinitionId);
        questions.splice(questionIndex + 1, 0, ...triggeredSubs);
      }
      var inactiveAnswerTriggers = question.answerTriggers.filter(at => at.answerValueId != val);
      removeTriggeredSubs(inactiveAnswerTriggers);

      // update state in the parent after callback has been called
      updateStepData(question);
    };

    if (otherText !== null) {
      question.answers[0].answerText = otherText;
      return updateStepData(question);
    } else if (question.answers.length) {
      question.answers[0].answerText = null;
    }

    if (!answerTrigger) {
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Please select an answer before attaching an image...',
          variant: 'error',
        },
      });
      return;
    }

    if (question.answers.length > 0) {
      if (question.answers[0].answerValueId === val && !image) {
        return;
      }
      const existingImages = question.answers[0].formFieldRepAnswerImage || [];
      let imageAlreadyAttached = false;
      let imageExistsInOtherAnswer = false;

      if (image) {
        imageAlreadyAttached = imageIsAttached(question, image.src, answerTrigger);
        imageExistsInOtherAnswer = imageIsAttachedToOtherAnswers(question, image.src);
      }

      question.answers[0].answerValueId = val;
      question.answers[0].value = answerTrigger.value;
      question.answers[0].formFieldRepAnswerImage =
        image && !imageAlreadyAttached && !imageExistsInOtherAnswer ? existingImages.concat(image) : existingImages;

      // stop execution here so answerTrigger inputs are not duplicated
      if (!!image) {
        return updateStepData(question);
      }
    } else {
      // var answer = {
      //   orderFormHeaderId: question.orderFormHeaderId,
      //   questionId: question.questionId,
      //   answerValueId: val,
      //   answerText: null,
      //   value: answerTrigger.value,
      //   formFieldRepAnswerImage: image ? [image] : [],
      // };
      // question.answers.push(answer);
    }

    // var triggeredSubs = answerTrigger.triggeredSub;
    // if (triggeredSubs != null && triggeredSubs.length > 0) {
    //   var questionIndex = questions.findIndex(q => q.formDefinitionId == question.formDefinitionId);
    //   questions.splice(questionIndex + 1, 0, ...triggeredSubs);
    // }
    // var inactiveAnswerTriggers = question.answerTriggers.filter(at => at.answerValueId != val);
    // removeTriggeredSubs(inactiveAnswerTriggers);

    if (answerTrigger.triggersFormHeaderId) {
      requestAddFormHeader(answerTrigger.triggersFormHeaderId, addAnswer);
    } else {
      addAnswer();
    }

    //if required and not already in there, then add image required
    // if (
    //   answerTrigger.imageRequired == 1 &&
    //   !question.answers.some(
    //     a =>
    //       a.orderFormHeaderId == question.orderFormHeaderId &&
    //       a.questionId == question.questionId &&
    //       a.formFieldRepAnswerImage != null &&
    //       a.formFieldRepAnswerImage.length > 0,
    //   )
    // ) {
    //   setShowSnack(true);
    //   setSnackMessage("This answer requires a photo. You will be asked to capture a photo in the 'Photos' step.");
    // }

    // update state in the parent
    updateStepData(question);
  };

  const updateCheckboxAnswerValue = (question, image = null, otherText = null) => {
    const currentAnswerValueId = question.answers.length ? question.answers[0].answerValueId : 4; // 4 is the answerValueId for a value of 'No', which is the default even if there are no answers
    const newAnswerValueId = question.answerTriggers.find(at => at.answerValueId != currentAnswerValueId).answerValueId;

    // find the answer value to
    // var val = question.answers.length ? question.answers[0].answerValueId : null;
    var answerTrigger = question.answerTriggers.find(at => at.answerValueId == newAnswerValueId);

    const addAnswer = () => {
      if (question.answers.length === 0) {
        // create a new answer object (which defaults to 'Yes' when there are no answers and the checkbox is clicked)
        var answer = {
          orderFormHeaderId: question.orderFormHeaderId,
          questionId: question.questionId,
          answerValueId: 3,
          answerText: null,
          value: 'Yes',
          formFieldRepAnswerImage: image ? [image] : [],
        };
        question.answers.push(answer);
      }

      var triggeredSubs = answerTrigger.triggeredSub;
      if (triggeredSubs != null && triggeredSubs.length > 0) {
        var questionIndex = questions.findIndex(q => q.formDefinitionId == question.formDefinitionId);
        questions.splice(questionIndex + 1, 0, ...triggeredSubs);
      }
      var inactiveAnswerTriggers = question.answerTriggers.filter(at => at.answerValueId != newAnswerValueId);
      removeTriggeredSubs(inactiveAnswerTriggers);

      // update state in the parent after callback has been called
      updateStepData(question);
    };

    if (otherText !== null) {
      question.answers[0].answerText = otherText;
      return updateStepData(question);
    } else if (question.answers.length) {
      question.answers[0].answerText = null;
    }

    if (question.answers.length > 0) {
      //toggle answer
      var currentAnswer = question.answers[0].answerValueId;

      // update only if no image is passed in (otherwise toggle it)
      if (!image) {
        question.answers[0].answerValueId = question.answerTriggers.find(
          at => at.answerValueId != currentAnswer,
        ).answerValueId;

        // if the answer was changed to 'No', remove the images
        if (question.answers[0].answerValueId === 4) {
          question.answers[0].formFieldRepAnswerImage = [];
        }
      } else {
        const existingImages = question.answers[0].formFieldRepAnswerImage || [];
        const imageAlreadyAttached = imageIsAttached(question, image.src);

        // add the answer value so if an image is dropped, it will be checked if it isn't already
        question.answers[0].answerValueId = 3;
        question.answers[0].formFieldRepAnswerImage = imageAlreadyAttached
          ? existingImages
          : existingImages.concat(image);

        // stop execution here (if not, answer triggering fires multiple times and duplicates inputs)
        if (!!image) {
          return updateStepData(question);
        }
      }
    } else {
      // var answer = {
      //   orderFormHeaderId: question.orderFormHeaderId,
      //   questionId: question.questionId,
      //   answerValueId: 3,
      //   answerText: null,
      //   value: 'Yes',
      //   formFieldRepAnswerImage: image ? [image] : [],
      // };
      // question.answers.push(answer);
    }

    if (answerTrigger.triggersFormHeaderId) {
      requestAddFormHeader(answerTrigger.triggersFormHeaderId, addAnswer);
    } else {
      addAnswer();
    }

    //if required and not already in there, then add image required
    // if (
    //   answerTrigger.imageRequired == 1 &&
    //   !question.answers.some(
    //     a =>
    //       a.orderFormHeaderId == question.orderFormHeaderId &&
    //       a.questionId == question.questionId &&
    //       a.formFieldRepAnswerImage != null &&
    //       a.formFieldRepAnswerImage.length > 0,
    //   )
    // ) {
    //   setShowSnack(true);
    //   setSnackMessage("This answer requires a photo. You will be asked to capture a photo in the 'Photos' step.");
    // }

    // update the state in the parent
    updateStepData(question);
  };

  const updateMultiAnswerValue = (question, val, image = null, otherText = null) => {
    var answerIndex = question.answers.findIndex(a => a.answerValueId == val);
    var answerTrigger = question.answerTriggers.find(at => at.answerValueId == val);

    // console.log({
    //   question,
    //   val,
    //   image,
    //   otherText,
    //   answerIndex,
    //   answerTrigger,
    // });

    if (otherText !== null) {
      question.answers[answerIndex].answerText = otherText;
      return updateStepData(question);
    }

    if (image && answerIndex !== -1) {
      const existingImages = question.answers[answerIndex].formFieldRepAnswerImage || [];
      const imageAlreadyAttached = imageIsAttached(question, image.src, answerTrigger);

      question.answers[answerIndex].answerValueId = val;
      question.answers[answerIndex].formFieldRepAnswerImage =
        image && !imageAlreadyAttached ? existingImages.concat(image) : existingImages;

      if (!!image) {
        return updateStepData(question);
      }
    } else if (answerIndex >= 0) {
      //remove it
      question.answers.splice(answerIndex, 1);
      var inactiveAnswerTrigger = question.answerTriggers.filter(at => at.answerValueId == val);

      console.log({
        inactiveAnswerTrigger,
      });
      removeTriggeredSubs(inactiveAnswerTrigger);
      // removeTriggeredSubs([answerTrigger]);
      //TODO remove image requirements
    } else {
      //add it - putting it in a function so I can pass it as a callback
      const addAnswer = () => {
        var answer = {
          orderFormHeaderId: question.orderFormHeaderId,
          questionId: question.questionId,
          answerValueId: val,
          value: answerTrigger.value,
          answerText: null,
          formFieldRepAnswerImage: image ? [image] : [],
        };
        question.answers.push(answer);
        var triggeredSubs = answerTrigger.triggeredSub;
        if (triggeredSubs != null && triggeredSubs.length > 0) {
          var questionIndex = questions.findIndex(q => q.formDefinitionId == question.formDefinitionId);
          questions.splice(questionIndex + 1, 0, ...triggeredSubs);
        }

        // update state in the parent after callback has been called
        updateStepData(question);
      };

      if (answerTrigger.triggersFormHeaderId) {
        // needs confirmation before selecting this answer
        requestAddFormHeader(answerTrigger.triggersFormHeaderId, addAnswer);
      } else {
        addAnswer();
      }

      //if required and not already in there, then add image required
      // if (
      //   answerTrigger.imageRequired == 1 &&
      //   !question.answers.some(
      //     a =>
      //       a.orderFormHeaderId == question.orderFormHeaderId &&
      //       a.questionId == question.questionId &&
      //       a.answerValueId == val &&
      //       a.formFieldRepAnswerImage != null &&
      //       a.formFieldRepAnswerImage.length > 0,
      //   )
      // ) {
      //   setShowSnack(true);
      //   setSnackMessage("This answer requires a photo. You will be asked to capture a photo in the 'Photos' step.");
      // }
    }

    // update state in the parent
    updateStepData(question);
  };

  const initTriggeredSubs = questionsArray => {
    //Performing with slice creates a copy of the original questions array just in case we pass in props.questions and it is self mutating
    questionsArray.slice().forEach(q => {
      if (q.answers.length > 0) {
        var answerTriggerDefinition = q.answerTriggers.filter(at =>
          q.answers.some(a => a.answerValueId == at.answerValueId),
        );
        var triggeredSubs = null;
        if (answerTriggerDefinition.length > 0) {
          triggeredSubs = [].concat
            .apply(
              [],
              answerTriggerDefinition.map(q => q.triggeredSub),
            )
            .filter(x => x);
        }

        if (
          triggeredSubs != null &&
          triggeredSubs[0] != null &&
          triggeredSubs.filter(
            ts =>
              !!ts &&
              !questions.some(
                pq => pq.formDefinitionId == ts.formDefinitionId && q.orderFormHeaderId == ts.orderFormHeaderId,
              ),
          ).length > 0
        ) {
          //filter triggered subs  already in questions:
          triggeredSubs = triggeredSubs.filter(
            ts =>
              !!ts &&
              !questions.some(
                pq => pq.formDefinitionId == ts.formDefinitionId && q.orderFormHeaderId == ts.orderFormHeaderId,
              ),
          );
          var questionIndex = questions.findIndex(
            pq => pq.formDefinitionId == q.formDefinitionId && pq.orderFormHeaderId == q.orderFormHeaderId,
          );
          questions.splice(questionIndex + 1, 0, ...triggeredSubs);
          updateStepQuestions(questions);
          initTriggeredSubs(triggeredSubs);
        }
      }
    });
  };

  const removeTriggeredSubs = inactiveAnswerTriggers => {
    //TODO remove these answers from databse if they have formanswerid
    inactiveAnswerTriggers.forEach(at => {
      if (at.triggeredSub != null) {
        at.triggeredSub.forEach(ts => {
          ts.answers = [];
          var questionIndex = questions.findIndex(q => q.formDefinitionId == ts.formDefinitionId);

          if (questionIndex >= 0) {
            questions.splice(questionIndex, 1);
          }
          removeTriggeredSubs(ts.answerTriggers);
        });
      }
    });
  };

  //TODO clean up names answer images vs image answers
  const saveAnswerImages = async (questionsToUpdate, orderLineId) => {
    var flattenedAnswerImages = [].concat.apply(
      [],
      questionsToUpdate.map(q => q.imageAnswers),
    );
    var results = await props.dataServices.orderApi.updateTriggeredFormAnswerImages(flattenedAnswerImages, orderLineId);
    //set new formFieldRepAnswerId
    //TODO only if FormFieldRepAnswerId == 0
    questionsToUpdate.forEach(qtu => {
      var newAnswers = results.filter(
        r => qtu.questionId == r.questionId && qtu.orderFormHeaderId == r.orderFormHeaderId,
      );
      if (newAnswers.length > 0) {
        qtu.imageAnswers = newAnswers;
      }
    });
    return results;
  };

  const saveAnswers = async (questionsToUpdate, orderLineId) => {
    var flattenedAnswers = [].concat.apply(
      [],
      questionsToUpdate.map(q => q.answers),
    );
    var results = await props.dataServices.orderApi.updateFormAnswers(flattenedAnswers, orderLineId);
    //set new formFieldRepAnswerId
    //TODO only if FormFieldRepAnswerId == 0
    questionsToUpdate.forEach(qtu => {
      var newAnswers = results.filter(
        r => qtu.questionId == r.questionId && qtu.orderFormHeaderId == r.orderFormHeaderId,
      );
      if (newAnswers.length > 0) {
        qtu.answers = newAnswers;
      }
    });
    return results;
  };

  const QUESTION_CONTAINER_HEIGHT = 60;
  const QUESTION_IMAGE_HEIGHT = 40;
  const DELETE_ICON_HEIGHT = 20;

  const getPictureCount = question =>
    question.answers.reduce((acc, cur) => {
      return acc + (cur.formFieldRepAnswerImage ? cur.formFieldRepAnswerImage.length : 0);
    }, 0);

  const getPictureCountLabel = question => {
    const count = getPictureCount(question);

    return count === 1 ? `(${count} photo)` : `(${count} photos)`;
  };

  const renderAttachedImage = (question, img, answerTriggerIndex) => (
    <div
      style={{
        position: 'relative',
        // marginBottom: -12,

        marginBottom: -3,
        marginTop: 6,
        // height: '100%',
      }}
    >
      <img
        src={img.src ? img.src : img.imageUri}
        style={{ width: 'auto', height: QUESTION_IMAGE_HEIGHT, marginRight: 20 }}
      />
      <span
        style={{
          position: 'absolute',
          top: -DELETE_ICON_HEIGHT / 2,
          right: DELETE_ICON_HEIGHT / 2,
          backgroundColor: 'rgba(255,0,0,0.8)',
          width: DELETE_ICON_HEIGHT,
          height: DELETE_ICON_HEIGHT,
          borderRadius: '50%',
          color: '#fff',
          fontFamily: '"Comic Sans MS", cursive, sans-serif',
          cursor: 'pointer',
          fontSize: 14,
        }}
        onClick={() => removeImageFromQuestion(question, img, answerTriggerIndex)}
      >
        X
      </span>
    </div>
  );

  /**
   * @func renderQuestionWithDropzone
   * @desc wrap a rendered question and add a drop zone for images to attach to
   * @param {*} input component to render on the left half of the screen
   * @param {*} options
   */
  const renderQuestionWithDropzone = (input, options = {}) => {
    const { isRadio, onDropImage, canDrop, question, answerTriggerIndex = null } = options;
    // const Input = React.cloneElement(input);

    let filteredImages = [];
    let imageRequired = false;

    if (question && question.answers && question.answers.length) {
      if (answerTriggerIndex !== null) {
        // find the answer trigger
        const answerTrigger = question.answerTriggers[answerTriggerIndex];

        // find the answers for this trigger
        // const triggerValue = question.answerTriggers[answerTriggerIndex].value;
        const triggerAnswers = question.answers.filter(a => a.value == answerTrigger.value);

        // get the images from these answers
        filteredImages = triggerAnswers.map(a => a.formFieldRepAnswerImage).flat();

        // find if image is required (if the answer trigger is active and image is required for that answer trigger)
        imageRequired = !!answerTrigger.imageRequired && !!triggerAnswers.length;
      } else {
        filteredImages = question.answers.map(a => a.formFieldRepAnswerImage).flat();
      }
    }

    if (isSelfInspect) {
      return (
        <div className={cn('flex-row', 'full-width')}>
          <div className="flex-1 col-start-start">
            <div className={cn('flex-1', 'align-stretch', 'row-start-center')}>{input}</div>
            {isSelfInspect && imageRequired && !filteredImages.length && (
              <div
                className={cn('flex-1', 'row-start-center')}
                style={{
                  marginTop: -10,
                  marginBototom: 10,
                  color: `var(--jmi-red)`,
                  opacity: 0.75,
                  fontSize: 12,
                }}
              >
                * Image required
              </div>
            )}
          </div>

          <div className="px-10 row-end-center">
            {imageRequired && (
              <>
                {
                  // !!filteredImages.length &&
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '2px solid var(--jmi-blue)',
                      width: 20,
                      height: 20,
                      marginRight: 15,
                      fontSize: 12,
                      color: 'var(--jmi-blue)',
                    }}
                  >
                    {filteredImages.length}
                  </span>
                }

                <MobilePhotoPicker
                  question={question}
                  answerTriggerIndex={answerTriggerIndex}
                  imageRequired={imageRequired}
                  images={filteredImages}
                  onAddImage={onDropImage}
                  imageIsAttached={imageIsAttached}
                  removeImageFromQuestion={removeImageFromQuestion}
                  iconStyle={{
                    color: filteredImages.length ? 'var(--jmi-blue)' : 'var(--jmi-red)',
                  }}
                />
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <DndZone
        question={question}
        isRadio={isRadio}
        onDropImage={onDropImage}
        canDrop={canDrop}
        answerTriggerIndex={answerTriggerIndex}
      >
        {({ isOver, canDrop, ref }) => (
          <div className={cn('flex-row', 'full-width')} style={{ background: isOver ? '#ddd' : 'transparent' }}>
            <div className={cn('flex-1', 'row-start-center')}>{input}</div>
            <div
              className={cn('flex-1', 'px-10', 'row-center-end')}
              style={{
                paddingTop: isRadio ? 1 : 25,
                marginBottom: isRadio ? 2 : 0,
                minWidth: 100,
              }}
            >
              <div
                ref={ref}
                className={cn('row-start-center')}
                style={{
                  // paddingTop: 15 + DELETE_ICON_HEIGHT / 2, // account for the delete icon so there is still equal space above and below the image
                  // paddingBottom: 15,
                  background: isOver ? (canDrop ? '#ddd' : 'rgba(255,0,0,0.2)') : 'transparent',
                  border: isOver ? '3px dashed #aaa' : '3px dashed #eee',
                  // borderLeft: imageRequired ? '3px solid rgba(255,0,0,042)' : '',
                  height: '100%',
                  minHeight: QUESTION_IMAGE_HEIGHT,
                  width: '100%',
                }}
              >
                {
                  <div
                    className={cn('row-start-center', 'align-stretch', 'flex-1')}
                    style={{ marginLeft: 10, paddingTop: 5 }}
                  >
                    {filteredImages.map(img => renderAttachedImage(question, img, answerTriggerIndex))}

                    {!filteredImages.length && imageRequired && (
                      <>
                        <img
                          src={addPhotoIcon}
                          style={{
                            width: 'auto',
                            height: QUESTION_IMAGE_HEIGHT - 10,
                            marginLeft: 10,
                            paddingRight: 20,
                            borderRight: '2px solid #bbb',
                          }}
                        />
                        <span style={{ color: '#F77', fontSize: '1rem', marginLeft: 20 }}>* image required</span>
                      </>
                    )}
                  </div>
                }
              </div>
            </div>
          </div>
        )}
      </DndZone>
    );
  };

  const multiSelectIsChecked = (answers, answerValueId) => {
    const answer = answers.find(a => a.answerValueId === answerValueId);
    if (answer) {
      // if (imageToEdit) {
      //   return answer.images && answer.images.some(i => i && i.formQaImageId === imageToEdit.formQaImageId);
      // }
      return true;
    } else {
      return false;
    }
  };

  // If one of the answers is "Other", show an "Other Details TextField"
  const otherAnswerExists = ({ answerType, answers, answerTriggers }, handleChange) => {
    const otherAnswer = answers.find(
      answer =>
        (answer.answerValueId === 12 || (answer.value && answer.value.substring(0, 5) === 'Other')) &&
        multiSelectIsChecked(answers, answer.answerValueId),
    );
    const onChange = evt => {
      handleChange(evt.target.value, otherAnswer.answerValueId);
    };

    if (otherAnswer) {
      return (
        <div className={classes.otherDetails}>
          <Typography
            variant="subtitle2"
            className={cn(classes.otherSubtitle, answerType === 'Single Select' && classes.otherSubtitleSingleSelect)}
            gutterBottom
          >
            Other Details
          </Typography>
          <TextField
            className={classes.otherDetailsTextField}
            value={otherAnswer.answerText}
            onChange={onChange}
            // onChange={event => handleChange('OtherTextField', event)}
            // onBlur={handleSave}
            margin="none"
            // disabled={isReadOnly}
            multiline
          />
        </div>
      );
    }
  };

  const renderQuestion = question => {
    if (
      //
      question.answerType == 'TextBox' ||
      question.answerType == 'TextArea' ||
      question.answerType == 'Address'
    ) {
      // check for links
      return renderQuestionWithDropzone(
        <ColoredTextField
          variant="outlined"
          key={question.formDefinitionId}
          label={question.question.value}
          onChange={evt => updateAnswerText(question, evt.target.value)}
          value={question.answers.length > 0 ? question.answers[0].answerText : ''}
          style={{
            marginTop: MARGIN_ABOVE_INPUT,
            width: '100%',
          }}
          multiline
        />,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (question.answerType == 'Number of items') {
      return renderQuestionWithDropzone(
        <ColoredTextField
          variant="outlined"
          multiline
          type="number"
          key={question.formDefinitionId}
          label={question.question.value}
          onChange={evt => updateAnswerText(question, evt.target.value)}
          value={question.answers.length > 0 ? question.answers[0].answerText : ''}
          style={{
            marginTop: MARGIN_ABOVE_INPUT,
            width: '100%',
          }}
        />,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (
      question.answerType == 'True/False' ||
      question.answerType == 'Yes/No' ||
      (question.answerType == 'Single Select' && question.answerTriggers.length <= 4)
    ) {
      return (
        <>
          <FormLabel style={{ marginTop: MARGIN_ABOVE_INPUT, color: 'black', marginLeft: 0 }}>
            {question.question.value}
          </FormLabel>
          <RadioGroup
            aria-label={question.question.value}
            name={question.question.value}
            // onChange={evt => updateAnswerValue(question, evt.target.value)}
            onChange={evt => updateAnswerValue(question, parseIntWithFallback(evt.target.value, null))}
            value={question.answers.length > 0 ? parseIntWithFallback(question.answers[0].answerValueId) : 0}
            className={cn('full-width')}
          >
            {question.answerTriggers.map((at, atIdx) =>
              renderQuestionWithDropzone(
                <ColoredFormControlLabel
                  key={at.answerValueId}
                  value={at.answerValueId}
                  label={at.value}
                  control={<ColoredRadio />}
                />,
                {
                  isRadio: true,
                  question,
                  answerTriggerIndex: atIdx,
                  onDropImage: image =>
                    updateAnswerValue(
                      question,
                      at.answerValueId,
                      // question.answers.length > 0 ? parseIntWithFallback(question.answers[0].answerValueId) : null,
                      image,
                    ),
                },
              ),
            )}
          </RadioGroup>
          {otherAnswerExists(question, text => updateAnswerValue(question, null, null, text))}
        </>
      );
    } else if (question.answerType == 'Single Select' && question.answerTriggers.length > 4) {
      return renderQuestionWithDropzone(
        <>
          {/* <FormLabel style={{ marginTop: MARGIN_ABOVE_INPUT }}>{question.question.value}</FormLabel> */}
          <div className="flex-column flex-1">
            <ColoredTextField
              id={question.question.value}
              select
              label={question.question.value}
              value={question.answers[0] && parseIntWithFallback(question.answers[0].answerValueId)}
              onChange={evt => updateAnswerValue(question, evt.target.value)}
              // helperText="Please select your currency"
              variant="outlined"
              style={{
                marginTop: MARGIN_ABOVE_INPUT,
                minWidth: 200,
              }}
            >
              {question.answerTriggers.map(at => (
                <MenuItem key={at.answerValueId} value={at.answerValueId}>
                  {at.value}
                </MenuItem>
              ))}
            </ColoredTextField>
            {otherAnswerExists(question, text => updateAnswerValue(question, null, null, text))}
          </div>
        </>,
        {
          question,
          onDropImage: image =>
            updateAnswerValue(
              question,
              question.answers[0] ? parseIntWithFallback(question.answers[0].answerValueId) : null,
              image,
            ),
        },
      );
    }

    //TODO handle other answers besides yes/no
    else if (question.answerType == 'Check Box') {
      // return null
      return (
        <>
          {renderQuestionWithDropzone(
            <div className={cn('row-start-center')} style={{ marginTop: MARGIN_ABOVE_INPUT }}>
              <FormControlLabel
                control={
                  <ColoredCheckbox
                    checked={
                      question.answers.length > 0 && parseIntWithFallback(question.answers[0].answerValueId) == 3
                    }
                    onChange={() => updateCheckboxAnswerValue(question)}
                  />
                }
                label={question.question.value}
                labelPlacement="end"
                style={
                  {
                    // marginLeft: 0,
                  }
                }
                classes={{
                  label: 'break-word text-left',
                }}
              />
              {otherAnswerExists(question, text => updateCheckboxAnswerValue(question, null, text))}
            </div>,
            {
              question,
              onDropImage: image => updateCheckboxAnswerValue(question, image),
            },
          )}
        </>
      );
    } else if (question.answerType == 'Multi Select') {
      return (
        <>
          <div
            className={cn('row-start-center')}
            style={{ marginTop: MARGIN_ABOVE_INPUT }}
            key={question.formDefinitionId}
          >
            <FormLabel className={cn()} component="legend">
              {question.question.value}
            </FormLabel>
          </div>

          {question.answerTriggers.map((at, atIdx) =>
            renderQuestionWithDropzone(
              <div key={at.answerValueId} style={{ flexDirection: 'row', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <ColoredCheckbox
                      checked={question.answers.findIndex(a => a.answerValueId == at.answerValueId) >= 0}
                      onChange={() => updateMultiAnswerValue(question, at.answerValueId)}
                    />
                  }
                  label={at.value}
                  labelPlacement="end"
                  style={
                    {
                      // marginLeft: 5,
                    }
                  }
                  classes={{
                    label: 'break-word text-left',
                  }}
                />
              </div>,
              {
                question,
                onDropImage: (image, atIndex) => updateMultiAnswerValue(question, at.answerValueId, image, atIndex),
                answerTriggerIndex: atIdx,
              },
            ),
          )}
          {otherAnswerExists(question, (text, answerValueId) =>
            updateMultiAnswerValue(question, answerValueId, null, text),
          )}
        </>
      );
    } else if (['Float', 'Percentage', 'Currency'].includes(question.answerType)) {
      // } else if (question.answerType == 'Currency') {
      // const startAdornment = answerTypeAdornments[question.answerType];

      const inputPropAdornments = answerTypeAdornments[question.answerType];

      return renderQuestionWithDropzone(
        <ColoredTextField
          variant="outlined"
          multiline
          key={question.formDefinitionId}
          label={question.question.value}
          onChange={evt => {
            const number = evt.target.value.split(',').join('');
            const parsedValue = parseNumberInputValue(number);

            if (parsedValue === null) return;

            return updateAnswerText(question, parsedValue);
          }}
          value={
            question.answers.length > 0
              ? formatNumberWithCommas(
                  question.answers[0].answerText,
                  //
                )
              : ''
          }
          // InputProps={{
          //   startAdornment,
          // }}
          InputProps={inputPropAdornments}
          style={{
            marginTop: MARGIN_ABOVE_INPUT,
            width: '100%',
          }}
        />,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (question.answerType == 'Hyperlink') {
      return (
        <h3
          key={question.formDefinitionId}
          className={cn('row-start-center')}
          style={{ marginTop: 5, marginBottom: 5 }}
        >
          Link:
          <a className={cn('account-dropdown-link')} target="_blank" href={question.answers[0]?.answerText}>
            {question.question?.value}
          </a>
        </h3>
      );
    } else if (question.answerType == 'Header') {
      // Section header: render only the question text, styled as a header
      return (
        <Typography
          key={question.formDefinitionId}
          variant="h6"
          style={{ fontWeight: 600, textDecoration: 'underline', marginTop: 16, marginBottom: 4 }}
        >
          {question.question?.value}
        </Typography>
      );
    } else if (question.answerType == 'Read Only') {
      // Informational question/answer carried over from a previous order; not editable
      const readOnlyAnswer = question.answers
        .map(
          a =>
            a.answerText ?? question.answerTriggers?.find(at => at.answerValueId == a.answerValueId)?.value,
        )
        .filter(Boolean)
        .join(', ');

      return (
        <div
          key={question.formDefinitionId}
          style={{ backgroundColor: '#f5f5f5', borderRadius: 4, padding: '8px 12px', marginTop: 5, marginBottom: 5 }}
        >
          <Typography variant="caption" style={{ color: 'gray' }}>
            {question.question?.value}
          </Typography>
          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
            {readOnlyAnswer || 'None'}
          </Typography>
        </div>
      );
    } else if (question.answerType == 'Date') {
      return renderQuestionWithDropzone(
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${question.formDefinitionId}`}
            autoOk
            inputVariant="outlined"
            format="MM/dd/yyyy"
            margin="normal"
            label={question.question.value}
            onChange={date => updateAnswerText(question, date || '')}
            value={question.answers.length > 0 ? question.answers[0].answerText : null}
            style={{ width: '100%' }}
          />
        </MuiPickersUtilsProvider>,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (question.answerType == 'Date MMYY') {
      return renderQuestionWithDropzone(
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${question.formDefinitionId}`}
            autoOk
            inputVariant="outlined"
            views={['year', 'month']}
            format="MM/yyyy"
            margin="normal"
            label={question.question.value}
            onChange={date => updateAnswerText(question, date || '')}
            value={question.answers.length > 0 ? question.answers[0].answerText : null}
            style={{ width: '100%' }}
          />
        </MuiPickersUtilsProvider>,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (question.answerType == 'Date YY' || question.answerType == 'Date YYYY') {
      const value =
        question.answers.length > 0 && question.answers[0].answerText
          ? new Date(question.answers[0].answerText, 0)
          : null;

      return renderQuestionWithDropzone(
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${question.formDefinitionId}`}
            autoOk
            inputVariant="outlined"
            views={['year']}
            format="yyyy"
            margin="normal"
            label={question.question.value}
            onChange={(fullDate, formattedDate) => {
              const dateIsEmpty = !fullDate;
              const validDate = dateIsEmpty || (fullDate instanceof Date && !isNaN(fullDate));

              if (!validDate) return;

              let dateValue = dateIsEmpty ? '' : parseInt(formattedDate).toString();

              updateAnswerText(question, dateValue);
            }}
            value={value}
            style={{ width: '100%' }}
          />
        </MuiPickersUtilsProvider>,
        {
          question,
          onDropImage: image =>
            updateAnswerText(question, question.answers.length > 0 ? question.answers[0].answerText : '', image),
        },
      );
    } else if (question.answerType == 'Picture') {
      return (
        <>
          {renderQuestionWithDropzone(
            <div className={cn('row-start-center')} style={{ marginTop: MARGIN_ABOVE_INPUT }}>
              <div>{question.question.value}</div>
            </div>,
            {
              question,
              onDropImage: image => updatePictureInput(question, image),
            },
          )}
        </>
      );
    } else {
      return null;
      // return <div key={question.formDefinitionId}>{question.question.value}</div>;
    }
  };

  const renderIsCommercialNarratives = () => (
    <h3
      style={{
        marginTop: 5,
        marginBottom: 5,
        textDecoration: 'underline',
      }}
    >
      <a className="account-dropdown-link" target="_blank" href="http://jmireports.cc/narrative/">
        Commercial Narratives Link: http://jmireports.cc/narrative/
      </a>
    </h3>
  );

  useEffect(() => {
    initTriggeredSubs(questions);
  }, []);

  return (
    <React.Fragment key="styleProviderReplacement">
      {/* <div id="field-rep-inspection-form-container" className={cn('flex-1', 'col-start-start', 'px-20')}> */}
      <div
        id="field-rep-inspection-form-container"
        className={props.isRCT ? cn('flex-1', 'col-start', 'px-20') : cn('flex-1', 'col-start-start', 'px-20')}
      >
        {isCommericialNarratives && renderIsCommercialNarratives()}

        {props.isRCT && <div dangerouslySetInnerHTML={{ __html: rctquestions }} />}

        {questions.map(q => renderQuestion(q))}

        <Snackbar style={classes.snackbar} visible={showSnack && useSnack} onDismiss={() => setShowSnack(false)}>
          {snackMessage}
        </Snackbar>
      </div>
    </React.Fragment>
  );
};

FieldRepInspectionForm.navigationOptions = {
  header: null,
};

FieldRepInspectionForm.propTypes = {
  stepData: PropTypes.shape({
    goToNextStep: PropTypes.func.isRequired,
    questions: PropTypes.array.isRequired,
    orderLineId: PropTypes.any.isRequired,
    useSnack: PropTypes.bool,
  }),
};

FieldRepInspectionForm.defaultProps = {
  // useSnack: true,
};

const styles = theme => ({
  question: {
    marginTop: 17,
    fontSize: 17,
  },
  snackbar: {
    position: 'absolute',
    margin: 5,
    //width:'100%',
    bottom: 5,
  },

  textField: {
    //
  },

  otherDetails: {
    marginLeft: '24px',
    maxWidth: 276, // 300 - marginLeft
    width: '100%',
    // width: 'calc((100vw - 460px) / 2)',
  },
  otherSubtitle: {
    fontWeight: '700',
    marginTop: '12px',
    textAlign: 'left',
  },
  otherSubtitleSingleSelect: {
    // marginTop: '24px',
  },
  otherDetailsTextField: {
    width: '100%',
  },
});

export default withStyles(styles)(FieldRepInspectionForm);
